import React, { useMemo } from 'react';
import { getPurchaseItems } from '../utils/purchaseItems';
import {
  buildReceiptItemDisplayName,
  formatReceiptMoney,
  formatVoucherQuantity,
} from '../utils/receiptFormatting';
import { buildScaleReadingBadge, isScaleWeightMetricUnit } from '../utils/scalePresentation';
import ScaleWeighingSummary from './ScaleWeighingSummary';

const resolveItemSubtotal = (item) => {
  const subtotal = Number(item?.subtotal);
  if (Number.isFinite(subtotal)) return subtotal;
  const unitPrice = Number(item?.price || 0) - Number(item?.discount || 0);
  return Math.floor(unitPrice * Number(item?.quantity || 0));
};

const PosClientView = ({
  purchase,
  loading,
  error,
  connectionStatus,
  hasEditor,
  scaleState,
  weighing,
}) => {
  const purchaseItems = useMemo(() => getPurchaseItems(purchase), [purchase]);
  const itemsSubtotal = Number(purchase?.itemsSubtotal || 0);
  const totalAmount = Number(purchase?.totalAmount || 0);
  const orderDiscountAmount = Number(purchase?.orderDiscountAmount || 0);
  const orderDiscountPercentage = Number(purchase?.orderDiscountPercentage || 0);
  const purchaseHeading = purchase?.id ? `Venta #${purchase.id}` : 'Venta nueva';
  const shouldShowScalePanel = Boolean(
    weighing
    || scaleState?.enabled
    || (scaleState?.connectionStatus && scaleState.connectionStatus !== 'idle')
  );
  const scaleBadge = useMemo(() => buildScaleReadingBadge(scaleState, {
    enabled: shouldShowScalePanel,
  }), [scaleState, shouldShowScalePanel]);
  const weighingWeightKg = scaleState?.freshStableWeightKg ?? scaleState?.latestStableWeightKg ?? null;
  const hasSelectedWeighing = Boolean(weighing);
  const weighingTitle = hasSelectedWeighing
    ? weighing?.varietyName || weighing?.productName || 'Producto en balanza'
    : 'Balanza';
  const weighingMeta = hasSelectedWeighing
    ? [weighing?.productName, weighing?.metricUnit]
      .filter((value, index, items) => Boolean(value) && items.indexOf(value) === index && value !== weighingTitle)
      .join(' · ')
    : '';
  const weighingUsesWeight = !hasSelectedWeighing || isScaleWeightMetricUnit(weighing?.metricUnit);
  const weighingAmountLabel = weighingUsesWeight ? undefined : (
    Number.isFinite(Number(weighingWeightKg)) ? formatVoucherQuantity(weighingWeightKg) : undefined
  );

  const weighingCard = scaleBadge ? (
    <section className={`sticky top-[14.5rem] z-10 rounded-2xl border px-4 py-3 shadow-md backdrop-blur ${scaleBadge.tone}`}>
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
        <div className="min-w-0 xl:w-64">
          <h2 className="truncate text-base font-semibold">{weighingTitle}</h2>
          {weighingMeta ? <p className="mt-0.5 truncate text-xs opacity-75">{weighingMeta}</p> : null}
        </div>
        <ScaleWeighingSummary
          weightKg={weighingWeightKg}
          unitPrice={hasSelectedWeighing ? weighing?.unitPrice : null}
          amountTitle={weighingUsesWeight ? 'Peso' : 'Cantidad'}
          amountLabel={weighingAmountLabel}
          allowZeroAmount
          className="flex-1 gap-2"
          cardClassName="rounded-xl bg-white/45 px-3 py-2"
          valueClassName="mt-1 text-3xl font-black tracking-tight sm:text-4xl"
        />
      </div>
    </section>
  ) : null;

  const renderPlaceholder = (content, className) => (
    <div className={`rounded-3xl border bg-white px-6 py-20 text-center shadow-sm ${className}`}>
      {content}
    </div>
  );

  let purchaseContent = null;

  if (loading) {
    purchaseContent = renderPlaceholder(
      <div className="text-gray-500">Cargando venta activa...</div>,
      'border-gray-200'
    );
  } else if (error) {
    purchaseContent = renderPlaceholder(
      <div className="text-red-700">No se pudo cargar la venta activa.</div>,
      'border-red-200 bg-red-50'
    );
  } else if (!purchase) {
    purchaseContent = renderPlaceholder(
      <>
        <div className="text-sm font-semibold uppercase tracking-wide text-gray-400">Vista cliente</div>
        <div className="mt-3 text-2xl font-semibold text-gray-800">
          {hasEditor ? 'No hay una venta activa para mostrar.' : 'No hay un editor POS conectado.'}
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Estado conexión: {connectionStatus === 'connected' ? 'conectado' : connectionStatus}
        </div>
      </>,
      'border-dashed border-gray-300'
    );
  } else {
    purchaseContent = (
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-6 py-5">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Venta activa</div>
            <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-700 sm:text-xl">{purchaseHeading}</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {purchase?.Client?.name || 'Sin cliente'}
                </p>
              </div>
              <div className="rounded-2xl bg-blue-50 px-4 py-3 text-right">
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-500">Total</div>
                <div className="mt-1 text-3xl font-bold text-blue-700">${formatReceiptMoney(totalAmount)}</div>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {purchaseItems.map((item, index) => {
              const subtotal = resolveItemSubtotal(item);
              const unitPrice = Number(item?.price || 0) - Number(item?.discount || 0);
              return (
                <div
                  key={`${item?.id || item?.varietyId || index}-${index}`}
                  className={`px-6 py-4 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {buildReceiptItemDisplayName(item)}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {item?.metricUnit || 'unidad'} · ${formatReceiptMoney(unitPrice)} x {formatVoucherQuantity(item?.quantity)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold uppercase tracking-wide text-gray-400">Subtotal</div>
                      <div className="mt-1 text-xl font-bold text-gray-900">${formatReceiptMoney(subtotal)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Comprobante</div>
          <div className="mt-5 space-y-4">
            <div className="flex items-center justify-between text-base text-gray-600">
              <span>Subtotal</span>
              <span className="font-semibold text-gray-900">${formatReceiptMoney(itemsSubtotal)}</span>
            </div>
            <div className="flex items-center justify-between text-base text-red-600">
              <span>Descuento</span>
              <span className="font-semibold">
                -${formatReceiptMoney(orderDiscountAmount)}
                {orderDiscountPercentage > 0 ? ` (${orderDiscountPercentage}%)` : ''}
              </span>
            </div>
            <div className="border-t border-dashed border-gray-200 pt-4">
              <div className="flex items-center justify-between text-lg font-semibold text-gray-700">
                <span>Total</span>
                <span className="text-3xl font-bold text-blue-700">${formatReceiptMoney(totalAmount)}</span>
              </div>
            </div>
          </div>
          <div className="mt-8 rounded-2xl bg-gray-50 px-4 py-4 text-sm text-gray-500">
            Los productos y totales se actualizan en tiempo real mientras la venta se edita.
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {weighingCard}
      {purchaseContent}
    </div>
  );
};

export default PosClientView;
